// Smart Daara - Physical school: levels & classes
import React, { useEffect, useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { physicalAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Trash2 } from 'lucide-react';
import { toast } from 'sonner';

export default function LevelsClasses() {
  const { language } = useLanguage();
  const fr = language === 'fr';
  const [levels, setLevels] = useState([]);
  const [classes, setClasses] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [levelName, setLevelName] = useState('');
  const [classForm, setClassForm] = useState({ name: '', level_id: '', room_id: '', main_teacher_id: '', max_students: 50 });

  const load = async () => {
    const [l, c, r, t] = await Promise.all([
      physicalAPI.listLevels(), physicalAPI.listClasses(), physicalAPI.listRooms(), physicalAPI.listTeachers(),
    ]);
    setLevels(l.data); setClasses(c.data); setRooms(r.data); setTeachers(t.data);
  };
  useEffect(() => { load().catch(() => {}); }, []);

  const addLevel = async () => {
    if (!levelName.trim()) return;
    await physicalAPI.createLevel({ name: levelName.trim(), order: levels.length });
    setLevelName(''); load();
    toast.success(fr ? 'Niveau créé' : 'Level created');
  };

  const addClass = async () => {
    if (!classForm.name || !classForm.level_id) { toast.error(fr ? 'Nom et niveau requis' : 'Name and level required'); return; }
    try {
      await physicalAPI.createClass({ ...classForm, room_id: classForm.room_id || null, main_teacher_id: classForm.main_teacher_id || null });
      setClassForm({ name: '', level_id: '', room_id: '', main_teacher_id: '', max_students: 50 });
      load();
      toast.success(fr ? 'Classe créée' : 'Class created');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  return (
    <div className="space-y-6" data-testid="levels-classes-page">
      <h1 className="text-2xl font-outfit font-bold text-slate-900">{fr ? 'Niveaux & Classes' : 'Levels & Classes'}</h1>

      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="border-0 shadow-sm">
          <CardHeader><CardTitle className="text-base">{fr ? 'Niveaux' : 'Levels'}</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="flex gap-2">
              <Input placeholder={fr ? 'ex. 6e, CP, Terminale' : 'e.g. Grade 6'} value={levelName} onChange={(e) => setLevelName(e.target.value)} data-testid="level-name-input" />
              <Button className="bg-emerald-700" onClick={addLevel} data-testid="add-level-btn">+</Button>
            </div>
            {levels.map((l) => (
              <div key={l.id} className="flex items-center justify-between px-3 py-2 bg-slate-50 rounded-lg">
                <span>{l.name}</span>
                <button onClick={async () => {
                  try { await physicalAPI.deleteLevel(l.id); load(); }
                  catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
                }} className="text-red-400 hover:text-red-600" data-testid={`delete-level-${l.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm lg:col-span-2">
          <CardHeader><CardTitle className="text-base">{fr ? 'Classes' : 'Classes'}</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid sm:grid-cols-6 gap-2 items-end">
              <div><Label className="text-xs">{fr ? 'Nom' : 'Name'}</Label><Input placeholder="6e A" value={classForm.name} onChange={(e) => setClassForm({ ...classForm, name: e.target.value })} data-testid="class-name-input" /></div>
              <div>
                <Label className="text-xs">{fr ? 'Niveau' : 'Level'}</Label>
                <select className="w-full h-10 border border-slate-200 rounded-md px-2 text-sm" value={classForm.level_id} onChange={(e) => setClassForm({ ...classForm, level_id: e.target.value })} data-testid="class-level-select">
                  <option value="">—</option>
                  {levels.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs">{fr ? 'Salle' : 'Room'}</Label>
                <select className="w-full h-10 border border-slate-200 rounded-md px-2 text-sm" value={classForm.room_id} onChange={(e) => setClassForm({ ...classForm, room_id: e.target.value })}>
                  <option value="">—</option>
                  {rooms.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs">{fr ? 'Prof principal' : 'Main teacher'}</Label>
                <select className="w-full h-10 border border-slate-200 rounded-md px-2 text-sm" value={classForm.main_teacher_id} onChange={(e) => setClassForm({ ...classForm, main_teacher_id: e.target.value })}>
                  <option value="">—</option>
                  {teachers.map((t) => <option key={t.user_id} value={t.user_id}>{t.first_name} {t.last_name}</option>)}
                </select>
              </div>
              <div><Label className="text-xs">{fr ? 'Effectif max' : 'Max'}</Label><Input type="number" value={classForm.max_students} onChange={(e) => setClassForm({ ...classForm, max_students: parseInt(e.target.value) || 50 })} /></div>
              <Button className="bg-emerald-700" onClick={addClass} data-testid="add-class-btn">{fr ? 'Créer' : 'Create'}</Button>
            </div>

            <div className="space-y-2">
              {classes.map((c) => (
                <div key={c.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 bg-slate-50 rounded-lg">
                  <div>
                    <span className="font-medium">{c.name}</span>
                    <span className="text-slate-400 text-sm ml-2">{c.level_name}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge variant="outline">{c.student_count} {fr ? 'élèves' : 'students'}</Badge>
                    <button onClick={async () => {
                      const res = await physicalAPI.deleteClass(c.id);
                      toast.success(res.data.archived ? (fr ? 'Classe archivée (élèves inscrits)' : 'Class archived') : (fr ? 'Classe supprimée' : 'Class deleted'));
                      load();
                    }} className="text-red-400 hover:text-red-600" data-testid={`delete-class-${c.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
              {classes.length === 0 && <p className="text-slate-400 text-sm text-center py-4">{fr ? 'Aucune classe' : 'No classes'}</p>}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
